/**
 * 郵便番号の正規化と、郵便番号→住所（都道府県+市区町村+町域）の lookup。
 * admin / hostess の名刺フォーム 4 動線（登録/編集）で共通利用する。
 *
 * 仕様: docs/database/20260619-bizcard-company-edit-target-spec.md（P3 / G7・G8）
 *
 * 住所 lookup は YubinBango データ（https://github.com/yubinbango/yubinbango）を使う。
 * yubinbango-data は日本郵便 CSV 由来の静的 JSONP を GitHub Pages で配信している
 * （CORS 許可済み・キー不要）。yubinbango 本体は h-adr の DOM 直接書き換えで React の
 * 制御コンポーネントと相性が悪いため、同じデータソースを fetch して使う。JSONP
 * ラッパー `$yubin({...});` は実行せず中身だけを JSON としてパースする
 * （リモートコードを評価しない）。
 *
 * データ形式: data/{郵便番号上3桁}.js → {"1040061": [13, "中央区", "銀座"], ...}
 *   配列は [都道府県JISコード, 市区町村, 町域, 街区(稀)]
 */

// ============================================================
// 郵便番号の正規化
// ============================================================

/**
 * 郵便番号入力の正規化: 全角数字を半角化し、数字7桁なら NNN-NNNN に整形する。
 * 7桁にならない入力はそのまま（trim して）返し、isValidPostalCode で弾く。
 * （本番 business_cards.postal_code の 97.4% がハイフン付きの既存慣行に合わせる）
 */
export function normalizePostalCodeInput(value: string): string {
  const digits = value
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[^0-9]/g, "");
  return digits.length === 7 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : value.trim();
}

/** 正規化済みの郵便番号が NNN-NNNN 形式か */
export function isValidPostalCode(normalized: string): boolean {
  return /^\d{3}-\d{4}$/.test(normalized);
}

// ============================================================
// 郵便番号 → 住所 lookup（YubinBango）
// ============================================================

const DATA_BASE = "https://yubinbango.github.io/yubinbango-data/data";

// JIS X 0401 都道府県コード（1〜47）。index 0 はダミー
const PREFECTURES = [
  "",
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
];

export interface AddressLookupResult {
  /** 都道府県+市区町村+町域（+街区）を結合した前置入力用文字列 */
  prefix: string;
  pref: string;
  city: string;
  town: string;
}

/**
 * 7桁郵便番号から住所を引く。見つからない・通信失敗・形式不正は null
 * （呼び出し側は自動入力をスキップして手入力にフォールバックする）
 */
export async function lookupAddressByPostalCode(
  digits: string,
  signal?: AbortSignal,
): Promise<AddressLookupResult | null> {
  if (!/^[0-9]{7}$/.test(digits)) return null;
  try {
    const res = await fetch(`${DATA_BASE}/${digits.slice(0, 3)}.js`, {signal});
    if (!res.ok) return null;
    const text = await res.text();
    const start = text.indexOf("(");
    const end = text.lastIndexOf(")");
    if (start < 0 || end <= start) return null;
    const map = JSON.parse(text.slice(start + 1, end)) as Record<string, unknown>;
    const entry = map[digits];
    if (!Array.isArray(entry) || entry.length === 0) return null;
    const pref = PREFECTURES[Number(entry[0])] ?? "";
    // 都道府県が解決できないエントリ（データ破損）は、部分住所
    // 「中央区銀座」だけを前置入力すると誤った住所になるため埋めない
    if (!pref) return null;
    const city = typeof entry[1] === "string" ? entry[1] : "";
    const town = typeof entry[2] === "string" ? entry[2] : "";
    const street = typeof entry[3] === "string" ? entry[3] : "";
    return {prefix: [pref, city, town, street].join(""), pref, city, town};
  } catch {
    // オフライン・abort・パース不能はすべて「自動入力なし」に倒す
    return null;
  }
}
